import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"];
import { createContext, useContext, useRef, useMemo, useCallback, useEffect, useSyncExternalStore } from 'react';
import { buildCardSectionRegistry } from '../registry/buildCardSectionRegistry';
import { useHasCardV2 } from 'crm-object-board-core/hooks';
import { useObjectTypeId } from 'crm-object-board-core/objectTypeDefinition';
import { useLoadBoardCardSectionSettings } from '../hooks/useLoadBoardCardSectionSettings';
import { useLoadCardSectionProperties } from '../hooks/useLoadCardSectionProperties';
import { useCardSectionRegistryMap } from '../registry/useCardSectionRegistryMap';
import { useWarmupAppSettingsFeaturesCache } from 'framework-app-settings-features-lib';
import useCardSectionsAdapter from '../sections/hooks/useCardSectionsAdapter';
import { useSelectedPipelineIdByAccess } from '../hooks/useSelectedPipelineIdByAccess';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
// Note: This provider is preloading the registry, user settings and required properties (used in search query)
function useStoreData({
  customRegistryMap,
  appId
}) {
  const objectTypeId = useObjectTypeId();
  const pipelineId = useSelectedPipelineIdByAccess();
  const hasCardV2 = useHasCardV2();
  const {
    loading: appSettingsLoading
  } = useWarmupAppSettingsFeaturesCache(['crm_object_pipelines', 'crm_board_settings', 'crmTagsSettings']);
  const neverLoadRegistry = !hasCardV2;
  const neverLoadCardSectionProperties = !hasCardV2;

  // registry map is dependent on app settings
  // we need to wait for app settings to load before loading the registry map
  const skipLoadingRegistryMap = neverLoadRegistry || appSettingsLoading;

  // getting pipelineId depends on app settings per pipeline access
  // we need to wait for app settings and pipelineId to load before fetching card settings
  const skipLoadingCardSectionSettings = neverLoadCardSectionProperties || appSettingsLoading;
  const {
    loading: loadingRegistryMap,
    data: registryMap
  } = useCardSectionRegistryMap({
    skip: skipLoadingRegistryMap,
    appId,
    customRegistryMap
  });
  const registryLoading = neverLoadRegistry ? false : appSettingsLoading || loadingRegistryMap || !registryMap;
  const registry = useMemo(() => {
    if (registryMap) {
      return buildCardSectionRegistry(registryMap);
    }
    return buildCardSectionRegistry(undefined);
  }, [registryMap]);
  const {
    data: cardSectionSettings,
    loading: cardSectionSettingsLoading
  } = useLoadBoardCardSectionSettings({
    pipelineId,
    objectTypeId,
    appId,
    skip: skipLoadingCardSectionSettings
  });
  const {
    data: cardSectionProperties,
    loading: cardSectionPropertiesLoading
  } = useLoadCardSectionProperties({
    cardSectionSettingsLoading,
    cardSectionSettings: cardSectionSettings || [],
    registry,
    registryLoading
  });
  const {
    cardContentSections,
    cardWrapperSections,
    cardActionSections
  } = useCardSectionsAdapter({
    registry,
    registryLoading,
    cardSectionSettings: cardSectionSettings || []
  });
  const store = useRef({
    appId,
    cardSectionProperties,
    cardSectionPropertiesLoading,
    cardSectionSettings: cardSectionSettings || [],
    cardSectionSettingsLoading,
    registry,
    registryLoading,
    cardContentSections,
    cardWrapperSections,
    cardActionSections
  });
  const get = useCallback(() => store.current, []);
  const subscribers = useRef(new Set());
  const subscribe = useCallback(callback => {
    subscribers.current.add(callback);
    return () => subscribers.current.delete(callback);
  }, []);

  // Notify subscribers when store changes
  useEffect(() => {
    store.current = {
      appId,
      cardSectionProperties,
      cardSectionPropertiesLoading,
      cardSectionSettings: cardSectionSettings || [],
      cardSectionSettingsLoading,
      registry,
      registryLoading,
      cardContentSections,
      cardWrapperSections,
      cardActionSections
    };
    subscribers.current.forEach(callback => callback());
  }, [appId, cardSectionProperties, cardSectionPropertiesLoading, cardSectionSettings, cardSectionSettingsLoading, registry, registryLoading, cardContentSections, cardWrapperSections, cardActionSections]);
  return useMemo(() => {
    return {
      get,
      subscribe
    };
  }, [get, subscribe]);
}
const StoreContext = /*#__PURE__*/createContext(null);
export function useCardPreloadStore(selector) {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('useCardPreloadStore must be inside CardPreloadProvider');
  }
  const state = useSyncExternalStore(store.subscribe, () => selector(store.get()));
  return state;
}
export function CardPreloadProvider(_ref) {
  let {
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  let alreadyHasContext = true;
  try {
    useCardPreloadStore(store => store.cardSectionProperties);
  } catch (e) {
    alreadyHasContext = false;
  }
  if (alreadyHasContext) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/_jsx(InternalCardPreloadProvider, Object.assign({}, rest, {
    children: children
  }));
}
function InternalCardPreloadProvider({
  children,
  customRegistryMap,
  appId
}) {
  const value = useStoreData({
    customRegistryMap,
    appId
  });
  return /*#__PURE__*/_jsx(StoreContext.Provider, {
    value: value,
    children: children
  });
}